import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { users, auth } from '../services/api';
import { toast } from 'react-toastify';
import { DashboardLayout } from '../components/Layout';
import TrainingReminderSettings from '../components/Pwa/TrainingReminderSettings';
import './ProfilePage.css';

export function ProfilePage() {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    avatar: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    receiveWhatsAppNotifications: false,
    whatsAppPhone: ''
  });

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        avatar: user.avatar || '',
        receiveWhatsAppNotifications: user.receiveWhatsAppNotifications || false,
        whatsAppPhone: user.whatsAppPhone || ''
      }));
    }
  }, [user]);

  // Resize image to 64x64 pixels
  const resizeImage = (file, maxSize = 64) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = maxSize;
          canvas.height = maxSize;
          const ctx = canvas.getContext('2d');
          
          // Draw image centered and cropped to square
          const size = Math.min(img.width, img.height);
          const x = (img.width - size) / 2;
          const y = (img.height - size) / 2;
          
          ctx.drawImage(img, x, y, size, size, 0, 0, maxSize, maxSize);
          
          // Convert to base64 with quality compression
          const resizedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
          resolve(resizedDataUrl);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Por favor selecione uma imagem válida.');
        return;
      }
      
      try {
        const resizedImage = await resizeImage(file, 64);
        setFormData({...formData, avatar: resizedImage});
        toast.success('Imagem carregada! Clique em Guardar para aplicar.');
      } catch (error) {
        toast.error('Erro ao processar imagem');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords if changing
    if (formData.newPassword) {
      if (formData.newPassword.length < 6) {
        toast.error('A password deve ter pelo menos 6 caracteres');
        return;
      }
      if (formData.newPassword !== formData.confirmPassword) {
        toast.error('As passwords não coincidem');
        return;
      }
    }

    setSaving(true);
    try {
      const updateData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        avatar: formData.avatar,
        receiveWhatsAppNotifications: formData.receiveWhatsAppNotifications,
        whatsAppPhone: formData.whatsAppPhone || null
      };

      if (formData.newPassword) {
        updateData.password = formData.newPassword;
      }

      await users.updateMe(updateData);

      // Update local user data
      if (updateUser) {
        updateUser({
          ...user,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          avatar: formData.avatar,
          receiveWhatsAppNotifications: formData.receiveWhatsAppNotifications,
          whatsAppPhone: formData.whatsAppPhone || null
        });
      }
      
      toast.success('Perfil atualizado com sucesso!');
      
      // Clear password fields
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao atualizar perfil');
    } finally {
      setSaving(false);
    }
  };

  const renderAvatar = () => {
    if (formData.avatar && (formData.avatar.startsWith('http') || formData.avatar.startsWith('data:'))) {
      return <img src={formData.avatar} alt="Avatar" className="profile-avatar-img" />;
    }
    return <span className="profile-avatar-letter">{formData.name?.charAt(0)?.toUpperCase() || 'U'}</span>;
  };

  return (
    <DashboardLayout userType={user?.role === 'Admin' ? 'admin' : 'client'}>
      <div className="profile-page">
        <div className="profile-header">
          <h2>O Meu Perfil</h2>
          <p>Gerir as informações da tua conta</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="profile-card">
            <h3>📷 Foto de Perfil</h3>
            
            <div className="avatar-section">
              <div className="profile-avatar-large">
                {renderAvatar()}
              </div>
              
              <div className="avatar-actions">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  style={{ display: 'none' }}
                  id="avatar-upload"
                />
                <label htmlFor="avatar-upload" className="btn btn-outline">
                  📁 Escolher Foto
                </label>
                {formData.avatar && (
                  <button 
                    type="button" 
                    className="btn btn-outline"
                    onClick={() => setFormData({...formData, avatar: ''})}
                  >
                    🗑️ Remover
                  </button>
                )}
                <p className="avatar-hint">JPG, PNG ou GIF. A imagem será redimensionada para 64x64px.</p>
              </div>
            </div>
          </div>

          <div className="profile-card">
            <h3>👤 Informações Pessoais</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="profile-name">Nome Completo</label>
                <input
                  id="profile-name"
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="profile-email">Email</label>
                <input
                  id="profile-email"
                  type="email"
                  className="form-input"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="profile-phone">Telefone</label>
                <input
                  id="profile-phone"
                  type="tel"
                  className="form-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="912 345 678"
                />
              </div>
            </div>
          </div>

          <div className="profile-card">
            <h3>💬 Notificações WhatsApp</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '16px', fontSize: '14px' }}>
              Recebe lembretes de aulas e avisos via WhatsApp
            </p>

            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <label className="toggle-switch" style={{ margin: 0 }}>
                <input
                  type="checkbox"
                  aria-label="Receber notificações por WhatsApp"
                  checked={formData.receiveWhatsAppNotifications}
                  onChange={(e) => setFormData({ ...formData, receiveWhatsAppNotifications: e.target.checked })}
                />
                <span className="toggle-slider" />
              </label>
              <span style={{ fontSize: '14px' }}>
                {formData.receiveWhatsAppNotifications ? '✅ Notificações WhatsApp ativas' : 'Notificações WhatsApp desativadas'}
              </span>
            </div>

            {formData.receiveWhatsAppNotifications && (
              <div className="form-group">
                <label htmlFor="profile-whatsapp">Número WhatsApp (opcional)</label>
                <input
                  id="profile-whatsapp"
                  type="tel"
                  className="form-input"
                  value={formData.whatsAppPhone}
                  onChange={(e) => setFormData({ ...formData, whatsAppPhone: e.target.value })}
                  placeholder="Se diferente do telefone principal, ex: 351912345678"
                />
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  Se vazio, usa o telefone principal. Inclui indicativo do país (ex: 351).
                </p>
              </div>
            )}
          </div>

          <TrainingReminderSettings />

          <div className="profile-card">
            <h3>🔐 Alterar Password</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '16px', fontSize: '14px' }}>
              Deixa em branco se não quiseres alterar a password
            </p>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="profile-new-password">Nova Password</label>
                <input
                  id="profile-new-password"
                  autoComplete="new-password"
                  type="password"
                  className="form-input"
                  value={formData.newPassword}
                  onChange={(e) => setFormData({...formData, newPassword: e.target.value})}
                  placeholder="Mínimo 6 caracteres"
                />
              </div>
              <div className="form-group">
                <label htmlFor="profile-confirm-password">Confirmar Password</label>
                <input
                  id="profile-confirm-password"
                  autoComplete="new-password"
                  type="password"
                  className="form-input"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  placeholder="Repetir password"
                />
              </div>
            </div>
          </div>

          <div className="profile-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'A guardar...' : '💾 Guardar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}

export default ProfilePage;
